'use strict';
import angular from 'angular';
import _ from 'lodash';

angular.module('core.export.services')

    .service('ExportReportFactory', ExportReportFactory);

/**
 * @ngInject
 */
function ExportReportFactory(
    Restangular,
    $q,
    $window,
    PubSub,
    DesignFactory,
    ScopeFactory,
    DateRangeFactory
) {
    var exports = Restangular.all('export');

    var $customReportModalScope;

    return {
        $registerCustomReportModalScope: $registerCustomReportModalScope,
        $initPdfOptions: $initPdfOptions,
        exportToPdf: exportToPdf,
        exportBuilderToPdf: exportBuilderToPdf,
        exportBuilderToPpt: exportBuilderToPpt,
        exportBuilderToSlides: exportBuilderToSlides,
        runBuilderToSlidesFlow: runBuilderToSlidesFlow,
        exportToPowerpoint: exportToPowerpoint,
        exportToExcel: exportToExcel,
        exportWidgetToExcel: exportWidgetToExcel,
        exportToPng: exportToPng,
        sendMail: sendMail,
        exportWidgetToCsv: exportWidgetToCsv
    };

    function $registerCustomReportModalScope(scope) {
        $customReportModalScope = ScopeFactory.$registerScope(scope, 'export-custom-report-modal');
    }

    /**
     * Proxy for initiating the pdf options modal
     */
    function $initPdfOptions() {
        angular.element('#export-custom-report-modal').modal('show');
        $customReportModalScope.init();
    }

    /**
     *
     * @param params.pageId
     * @param params.layoutId
     * @param params.options
     * @returns {*}
     */
    function exportToPdf(params) {
        if (window.isNUI && !params.html) {
            PubSub.emit('SegmentEvents', {
                event: 'BuildPDFReportDownloadEvent',
                payload: {page: DesignFactory.getCurrentPage(), options: params }
            });
        }
        return exportEntity('pdf', params);
    }

    /**
     *
     * @param params.pageId
     * @param params.layoutId
     * @param params
     * @returns {*}
     */
    function exportBuilderToPdf(params) {
        return exportEntity('builder', params);
    }

    /**
     *
     * @param params
     * @returns {*}
     */
    function exportBuilderToPpt(params) {
        return exportEntity('builder/powerpoint', params);
    }

    /**
     * @param params
     * @returns {*}
     */
    function exportBuilderToSlides(params) {
        return exportEntity('builder/slides', params);
    }

    /**
     * Opens the Slides OAuth popup, then runs the export+upload as a Restangular call
     * once the popup posts back with its nonce. Mirrors the Vue/NUI pattern: the OAuth
     * callback is fast and idempotent; heavy work runs as a normal authenticated XHR.
     *
     * @param {object} params same shape as exportBuilderToSlides()
     * @returns {Promise<string>} resolves with the Drive webViewLink
     */
    function runBuilderToSlidesFlow(params) {
        var deferred = $q.defer();

        var request = exportBuilderToSlides(params);
        var baseUrl = request.getRequestedUrl();
        var reqParams = request.reqParams || {};
        var queryString = Object.keys(reqParams)
            .filter(function (key) { return reqParams[key] !== undefined && reqParams[key] !== null; })
            .map(function (key) { return encodeURIComponent(key) + '=' + encodeURIComponent(reqParams[key]); })
            .join('&');

        var popupUrl = baseUrl + (queryString ? '?' + queryString : '');
        var popup = $window.open(
            'about:blank',
            'tapclicks_slides_oauth',
            'popup=yes,width=560,height=720'
        );

        // Paint a same-origin "Connecting to Google…" splash so the popup isn't blank
        // during the multi-hop OAuth redirect chain.
        if (popup) {
            try {
                popup.document.open();
                popup.document.write(
                    '<!doctype html><html><head><meta charset="utf-8">'
                    + '<title>Connecting to Google…</title>'
                    + '<style>html,body{height:100%;margin:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;background:#fafafa;color:#444}'
                    + '.wrap{height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px}'
                    + '.spinner{width:40px;height:40px;border:3px solid #ddd;border-top-color:#3273dc;border-radius:50%;animation:spin 1s linear infinite}'
                    + '@keyframes spin{to{transform:rotate(360deg)}}</style></head>'
                    + '<body><div class="wrap"><div class="spinner"></div><div>Connecting to Google…</div></div></body></html>'
                );
                popup.document.close();
            } catch (e) {
                // Fall through; popup will just be blank as before.
            }
            popup.location.href = popupUrl;
        }

        var settled = false;
        var popupTimer = null;

        function cleanup() {
            $window.removeEventListener('message', onMessage);
            if (popupTimer) {
                $window.clearInterval(popupTimer);
                popupTimer = null;
            }
        }

        function onMessage(event) {
            if (settled) return;
            if (event.origin !== $window.location.origin) return;
            var data = event.data;
            if (!data || typeof data !== 'object') return;
            if (data.type !== 'oauth-slides-ready' && data.type !== 'oauth-slides-error') return;
            if (data.handler !== 'reportstudio') return;

            settled = true;
            cleanup();

            if (data.type === 'oauth-slides-error') {
                deferred.reject(new Error(data.message || 'Google Slides export failed.'));
                return;
            }

            // Validate the nonce shape before sending it back to the API.
            if (!/^[0-9a-f]{64}$/.test(String(data.nonce))) {
                deferred.reject(new Error('Invalid Google Slides export nonce.'));
                return;
            }

            exports.one('builder/slides').one(data.nonce).customPOST({}, 'run')
                .then(function (result) {
                    var link = result && result.webViewLink;
                    if (!link) {
                        deferred.reject(new Error('Google Slides export did not return a link.'));
                        return;
                    }
                    deferred.resolve(link);
                })
                .catch(function (err) {
                    var msg = (err && err.data && err.data.data && err.data.data[0])
                        || (err && err.message)
                        || 'Google Slides export failed.';
                    deferred.reject(new Error(msg));
                });
        }
        $window.addEventListener('message', onMessage);

        // Reject when the popup is closed without posting (user-cancelled), with a
        // brief grace window since popups close immediately after their postMessage.
        popupTimer = $window.setInterval(function () {
            if (popup && popup.closed) {
                $window.clearInterval(popupTimer);
                popupTimer = null;
                $window.setTimeout(function () {
                    if (settled) return;
                    settled = true;
                    cleanup();
                    deferred.reject(new Error('Google Slides export was cancelled.'));
                }, 1000);
            }
        }, 500);

        return deferred.promise;
    }

    /**
     *
     * @param params.pageId
     * @param params.layoutId
     * @returns {*}
     */
    function exportToPowerpoint(params) {
        return exportEntity('powerpoint', params);
    }

    /**
     *
     * @param params.pageId
     * @param params.layoutId
     * @returns {*}
     */
    function exportToExcel(params) {
        return exportEntity('excel', params);
    }

    /**
     *
     * @param params.pageId
     * @param params.layoutId
     * @returns {*}
     */
    function exportWidgetToExcel(params) {
        return exportEntity('excel/widget', params);
    }

    /**
     *
     * @param params.pageId
     * @param params.layoutId
     * @returns {*}
     */
    function exportWidgetToCsv(params) {
        return exportEntity('csv/widget', params);
    }

    /**
     *
     * @param id
     * @returns {*}
     */
    function exportToPng(params) {
        return exportEntity('png', params);
    }

    /**
     * Helper function build export entity request
     * @param type
     * @param params
     * @param params.pageId
     * @param params.layoutId
     * @param params.pageEntity
     * @param params.widgetId
     * @returns {*}
     */
    function exportEntity(type, params) {
        var request;

        if ('pageId' in params) {
            if ('layoutId' in params) {
                request = exports.one(type, params.pageId).all(params.layoutId);
            }
            else {
                request = exports.one(type, params.pageId);
            }
        } else if ('pageEntity' in params) {
            request = exports.all(type).all('dynamic').one(params.pageEntity);
            delete params['pageEntity'];
        }

        if ('widgetId' in params) {
            request = exports.one(type, params.widgetId);
        }

        // We need to build the request here manually, because we do not
        // want to trigger the request using a .get()
        // the $.fileDownload() will make the request
        request.reqParams = _.assign({daterange: DateRangeFactory.buildDateQueryParam()}, params);
        return request;
    }

    /**
     * @param params
     * @returns {*}
     */
    function sendMail(params) {
        return exports.one('builder').one('sendmail').post(params.pageId, params);
    }
}